import { Main, Models, StatCard, DataTable, Assets } from 'utils/imports.utils';
import MaskIcon from 'common_components/ui/mask_icon/mask_icon.ui';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import AvatarInitials from 'common_components/ui/avatar_initials/avatar_initials.ui';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetState, formatViewCount, getAdminUserLabel, truncateText } from 'utils/functions.utils';
import { ADMIN_TEXT_MAX_CHARS } from 'utils/constant.utils';
import { AdminUserRow, AdminOverviewStats } from 'interfaces/admin.interface';
import './admin_overview.screen.scss';

const PREVIEW_LIMIT = 8;

const AdminOverview = () => {
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    overview: null as AdminOverviewStats | null,
    recentUsers: [] as AdminUserRow[],
    loading: true,
  });

  const fetchOverview = async () => {
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const [usersRes, overviewRes]: any = await Promise.all([
        Models.auth.adminListUsers({ skip: 0, limit: PREVIEW_LIMIT, role: 'user' }),
        Models.auth.adminGetOverview(),
      ]);
      setState({
        overview: overviewRes?.data || null,
        recentUsers: usersRes?.data?.docs || [],
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch admin overview stats', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchOverview();
    } else {
      navigate('/login');
    }
  }, []);

  const userColumns: IDataTableColumn[] = [
    {
      key: 'name',
      label: 'Name',
      render: (row: AdminUserRow) => (
        <span className="admin_overview_user_name">
          <AvatarInitials name={getAdminUserLabel(row)} size={26} />
          <span title={row.username || undefined}>{truncateText(row.username || '—', ADMIN_TEXT_MAX_CHARS)}</span>
        </span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row: AdminUserRow) => row.email || '—',
    },
    { key: 'project_count', label: 'Projects', align: 'right' },
  ];

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Overview" title="Overview">
        <div className="admin_overview">
          <div className="admin_overview_stats">
            <StatCard icon={<MaskIcon src={Assets.admin_nav_users} size={16} />} label="Users" value={state.overview?.total_users || 0} />
            <StatCard icon={<MaskIcon src={Assets.admin_nav_projects} size={16} />} label="Projects" value={state.overview?.total_projects || 0} />
            <StatCard
              icon={<MaskIcon src={Assets.admin_stat_views} size={16} />}
              label="Target views"
              value={formatViewCount(state.overview?.total_views)}
            />
            <StatCard icon={<MaskIcon src={Assets.admin_nav_tickets} size={16} />} label="Open tickets" value={state.overview?.open_tickets || 0} />
          </div>

          <div className="admin_overview_card">
            <div className="admin_overview_card_header">
              <span className="admin_overview_card_title">Recent users</span>
              <button className="admin_overview_view_all" onClick={() => navigate('/admin/users')}>
                View all
              </button>
            </div>
            <DataTable
              columns={userColumns}
              rows={state.recentUsers}
              rowKey="_id"
              loading={state.loading}
              emptyMessage="No users found"
              onRowClick={(row: AdminUserRow) => navigate(`/admin/users/${row._id}`)}
            />
          </div>
        </div>
      </AdminPage>
    </Main>
  );
};

export default AdminOverview;
